import React, { useEffect, useState } from 'react';
import './Header.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { HiOutlineShieldCheck, HiOutlineMapPin, HiOutlineUser, HiOutlineShoppingCart, HiOutlineUser as HiOutlineUserProfile, HiOutlineDocumentText, HiOutlineDevicePhoneMobile, HiOutlineClock, HiOutlineShoppingBag, HiOutlineEnvelope, HiOutlineGlobeAlt } from 'react-icons/hi2';
import KhulegLogo from './KhulegLogo';
import api from '../utils/api';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import ForgotUsernameModal from './ForgotUsernameModal';
import Contact from './Contact';
import ChatModal from './ChatModal';
import resolveMediaUrl from '../utils/mediaUrl';
import AddCarModal from './AddCarModal';
import ServiceBookingModal from './ServiceBookingModal';

const noop = () => {};

const looksLikeImageAsset = (value) => /\.(png|jpe?g|webp|gif|bmp|avif|svg)(\?|#|$)/i.test(String(value || ''));

const formatHistoryTime = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('mn-MN');
};

const EyeIcon = ({ open = false }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="secret-toggle-icon">
    {open ? (
      <>
        <path
          d="M2.25 12S5.25 6.75 12 6.75 21.75 12 21.75 12 18.75 17.25 12 17.25 2.25 12 2.25 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </>
    ) : (
      <>
        <path
          d="M3 3 21 21"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M10.58 10.58A2 2 0 0 0 13.42 13.42"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.88 5.09A10.94 10.94 0 0 1 12 4.88C18.75 4.88 21.75 12 21.75 12A20.06 20.06 0 0 1 18.56 16.19M14.12 18.91A10.94 10.94 0 0 1 12 19.12C5.25 19.12 2.25 12 2.25 12A19.61 19.61 0 0 1 5.71 7.56"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
);

const Header = ({
  variant = 'default',
  showAdLink = false, // landing дээр зар нэмэхийг бүрэн хориглоно
  showQuickCreate = !!(variant !== 'landing'),
  useLandingWelcomeStyles = variant === 'landing',
  onOpenLogin = noop,
  onOpenRegister = noop,
  onOpenForgot = noop,
  onOpenChat = noop,
  onOpenContact = noop,
  themeMode = 'night',
  themeMenuOpen = false,
  onToggleThemeMenu = noop,
  onCloseThemeMenu = noop,
  onApplyTheme = noop
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, updateUser, refreshUser } = useAuth();
  const [localModal, setLocalModal] = useState('');
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [showProfileInfo, setShowProfileInfo] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState('');
  const [openNestedDropdownKey, setOpenNestedDropdownKey] = useState('');
  const [isCompactScreen, setIsCompactScreen] = useState(() => window.innerWidth <= 768);
  const [walletMenuOpen, setWalletMenuOpen] = useState(false);
  const [walletAction, setWalletAction] = useState('');
  const [walletSummary, setWalletSummary] = useState({
    balance: 0,
    adminBankName: '',
    adminIban: '',
    adminBankAccountNumber: ''
  });
  const [walletMessage, setWalletMessage] = useState('');
  const [walletError, setWalletError] = useState('');
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletWithdrawForm, setWalletWithdrawForm] = useState({
    bankName: '',
    iban: '',
    bankAccountNumber: '',
    amount: '',
    walletPin: ''
  });
  const [walletTransferForm, setWalletTransferForm] = useState({
    targetUserId: '',
    amount: '',
    walletPin: ''
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [walletBalanceVisible, setWalletBalanceVisible] = useState(false);
  const [activeProfileSection, setActiveProfileSection] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(() => localStorage.getItem('khuleg.profilePreviewUrl') || '');
  const [accountDeleteModalOpen, setAccountDeleteModalOpen] = useState(false);
  const [accountDeleteReason, setAccountDeleteReason] = useState('');
  const [accountDeleteLoading, setAccountDeleteLoading] = useState(false);
  const [accountDeleteMessage, setAccountDeleteMessage] = useState('');
  const [accountDeleteError, setAccountDeleteError] = useState('');
  const [accountDeletionContact, setAccountDeletionContact] = useState({
    phone: '',
    supportEmail: ''
  });
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [devicesModalOpen, setDevicesModalOpen] = useState(false);
  const [registeredDevices, setRegisteredDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState('');
  const [devicesMessage, setDevicesMessage] = useState('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [viewHistoryItems, setViewHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyMessage, setHistoryMessage] = useState('');
  const [likedModalOpen, setLikedModalOpen] = useState(false);
  const [likedItems, setLikedItems] = useState([]);
  const [likedLoading, setLikedLoading] = useState(false);
  const [likedError, setLikedError] = useState('');
  const [addCarModalOpen, setAddCarModalOpen] = useState(false);
  const [serviceBookingModalOpen, setServiceBookingModalOpen] = useState(false);
  const [carMenuOpen, setCarMenuOpen] = useState(false);

  const loadWalletSummary = async () => {
    if (!user) return;

    try {
      const response = await api.get('/wallet/summary');
      let nextSummary = {
        balance: Number(response.data?.summary?.balance || 0),
        adminBankName: response.data?.summary?.adminBankName || '',
        adminIban: response.data?.summary?.adminIban || '',
        adminBankAccountNumber: response.data?.summary?.adminBankAccountNumber || ''
      };

      if (!nextSummary.adminBankName && !nextSummary.adminIban && !nextSummary.adminBankAccountNumber) {
        const fallback = await api.get('/admin/public');
        nextSummary = {
          ...nextSummary,
          adminBankName: fallback.data?.info?.bankName || '',
          adminIban: fallback.data?.info?.iban || '',
          adminBankAccountNumber: fallback.data?.info?.bankAccountNumber || ''
        };
      }

      setWalletSummary(nextSummary);
      updateUser((prev) => (prev ? { ...prev, walletBalance: nextSummary.balance } : prev));
    } catch (_error) {
      setWalletError('Хэтэвчийн мэдээлэл уншиж чадсангүй');
    }
  };

  useEffect(() => {
    if (!walletMenuOpen || !user) return;

    loadWalletSummary();
  }, [walletMenuOpen, user, updateUser]);

  useEffect(() => {
    const onResize = () => {
      const compact = window.innerWidth <= 768;
      setIsCompactScreen(compact);
      if (!compact) {
        setOpenDropdownKey('');
        setOpenNestedDropdownKey('');
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!user || user.publicUserId) {
      return;
    }

    refreshUser().catch(() => {});
  }, [user, refreshUser]);

  useEffect(() => {
    if (!user) {
      setSupportUnreadCount(0);
      return undefined;
    }

    let cancelled = false;

    const loadSupportSummary = async () => {
      try {
        const response = await api.get('/support/summary');
        if (!cancelled) {
          setSupportUnreadCount(Number(response.data?.summary?.unreadCount || 0));
        }
      } catch (_error) {
        if (!cancelled) {
          setSupportUnreadCount(0);
        }
      }
    };

    loadSupportSummary();
    const intervalId = window.setInterval(loadSupportSummary, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [user, localModal]);

  useEffect(() => {
    const handleAdminBankUpdated = () => {
      if (walletMenuOpen) {
        loadWalletSummary();
      }
    };

    const handleStorage = (event) => {
      if (event.key === 'khuleg.admin.bank.updatedAt' && walletMenuOpen) {
        loadWalletSummary();
      }
    };

    window.addEventListener('khuleg-admin-bank-updated', handleAdminBankUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('khuleg-admin-bank-updated', handleAdminBankUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [walletMenuOpen, user]);

  const profileSections = [
    { key: 'wallet', label: 'Хэтэвч' },
    { key: 'devices', label: 'Бүртгэлтэй төхөөрөмж' },
    { key: 'username', label: 'Профайл нэр' },
    { key: 'password', label: 'Нууц үг' },
    { key: 'email', label: 'Майл хаяг' },
    { key: 'phone', label: 'Утас' },
  ];

  const toggleProfileSection = (key, event) => {
    event.stopPropagation();
    setActiveProfileSection(prev => (prev === key ? null : key));
  };

  // Бүх хэрэглэгч энгийн хэрэглэгч тул role = 'user', canUseQuickCreate = false
  const role = 'user';
  const canUseQuickCreate = false;
  const profileRoleLabel = role === 'admin' ? 'Admin' : role === 'moderator' ? 'Moderator' : 'User';
  const profileRoleIcon = role === 'admin' ? <HiOutlineShieldCheck className="profile-role-icon" /> : role === 'moderator' ? <HiOutlineMapPin className="profile-role-icon" /> : <HiOutlineUser className="profile-role-icon" />;

  const openLogin = onOpenLogin === noop ? () => setLocalModal('login') : onOpenLogin;
  const openRegister = onOpenRegister === noop ? () => setLocalModal('register') : onOpenRegister;
  const openForgot = onOpenForgot === noop ? () => setLocalModal('forgot-choice') : onOpenForgot;
  const openChat = onOpenChat === noop ? () => setLocalModal('chat') : onOpenChat;
  const openContact = onOpenContact === noop ? () => setLocalModal('contact') : onOpenContact;
  const adminPortalUrl = String(process.env.REACT_APP_ADMIN_PORTAL_URL || '').trim() || '/admin';
  const isExternalAdminPortal = /^https?:\/\//i.test(adminPortalUrl);

  const loadRegisteredDevices = async () => {
    setDevicesLoading(true);
    setDevicesError('');
    try {
      const response = await api.get('/auth/devices');
      setRegisteredDevices(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (requestError) {
      setDevicesError(requestError.response?.data?.message || 'Төхөөрөмжийн мэдээлэл уншиж чадсангүй');
    } finally {
      setDevicesLoading(false);
    }
  };

  const openDevicesModal = async () => {
    setDevicesMessage('');
    setDevicesError('');
    setDevicesModalOpen(true);
    await loadRegisteredDevices();
  };

  const loadViewHistory = async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await api.get('/history');
      setViewHistoryItems(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (requestError) {
      setHistoryError(requestError.response?.data?.message || 'Үзсэн түүх уншиж чадсангүй');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryModal = async () => {
    setHistoryMessage('');
    setHistoryError('');
    setHistoryModalOpen(true);
    await loadViewHistory();
  };

  const removeHistoryItem = async (id) => {
    setHistoryMessage('');
    setHistoryError('');
    try {
      const response = await api.delete(`/history/${id}`);
      setHistoryMessage(response.data?.message || 'Түүхээс устгалаа');
      await loadViewHistory();
    } catch (requestError) {
      setHistoryError(requestError.response?.data?.message || 'Түүхээс устгах үед алдаа гарлаа');
    }
  };

  const clearHistory = async () => {
    setHistoryMessage('');
    setHistoryError('');
    try {
      const response = await api.delete('/history');
      setHistoryMessage(response.data?.message || 'Үзсэн түүхийг цэвэрлэлээ');
      setViewHistoryItems([]);
    } catch (requestError) {
      setHistoryError(requestError.response?.data?.message || 'Үзсэн түүх цэвэрлэх үед алдаа гарлаа');
    }
  };

  const openHistoryItem = (item) => {
    const mediaUrl = String(item?.mediaUrl || '').trim();
    if (mediaUrl) {
      window.open(resolveMediaUrl(mediaUrl), '_blank', 'noopener,noreferrer');
      return;
    }

    if (item?.itemType === 'movie') {
      navigate('/movies');
      setHistoryModalOpen(false);
      return;
    }

    const search = new URLSearchParams();
    if (item?.section) {
      search.set('contentType', item.section);
    }
    if (item?.category) {
      search.set('category', item.category);
    }

    navigate(`/feed${search.toString() ? `?${search.toString()}` : ''}`);
    setHistoryModalOpen(false);
  };

  const loadLikedContent = async () => {
    setLikedLoading(true);
    setLikedError('');
    try {
      const response = await api.get('/feedback/liked');
      setLikedItems(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (requestError) {
      setLikedError(requestError.response?.data?.message || 'Таалагдсан контент уншиж чадсангүй');
    } finally {
      setLikedLoading(false);
    }
  };

  const openLikedContentModal = async () => {
    setLikedError('');
    setLikedModalOpen(true);
    await loadLikedContent();
  };

  const openLikedItem = (item) => {
    if (item?.targetType === 'movie') {
      navigate('/movies');
    } else {
      const params = new URLSearchParams();
      if (item?.section) params.set('contentType', item.section);
      if (item?.category) params.set('category', item.category);
      navigate(`/feed${params.toString() ? `?${params.toString()}` : ''}`);
    }
    setLikedModalOpen(false);
  };

  const disconnectDeviceSession = async (sessionId) => {
    setDevicesMessage('');
    setDevicesError('');
    try {
      const response = await api.delete(`/auth/devices/${sessionId}`);
      setDevicesMessage(response.data?.message || 'Төхөөрөмжийн холболтыг салгалаа');
      await loadRegisteredDevices();
    } catch (requestError) {
      setDevicesError(requestError.response?.data?.message || 'Төхөөрөмжийн холболт салгах үед алдаа гарлаа');
    }
  };

  const handleProfilePreviewChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    // Limit: 200KB (204800 bytes) for Data URL string
    const MAX_DATA_URL_SIZE = 204800;
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (result.length > MAX_DATA_URL_SIZE) {
        alert('Зураг хэтэрхий том байна. 200KB-аас бага хэмжээтэй зураг сонгоно уу.');
        setProfilePreviewUrl('');
        localStorage.removeItem('khuleg.profilePreviewUrl');
        return;
      }
      setProfilePreviewUrl(result);
      try {
        localStorage.setItem('khuleg.profilePreviewUrl', result);
      } catch (e) {
        alert('Зургийг хадгалах боломжгүй байна. Хөтчийн санах ой дүүрсэн байж магадгүй.');
      }
    };
    reader.readAsDataURL(selected);
  };

  const resetWalletFeedback = () => {
    setWalletMessage('');
    setWalletError('');
  };

  const openAccountDeletionModal = async () => {
    setAccountDeleteModalOpen(true);
    setAccountDeleteError('');
    setAccountDeleteMessage('');

    try {
      const response = await api.get('/admin/public');
      setAccountDeletionContact({
        phone: response.data?.info?.phone || '',
        supportEmail: response.data?.info?.supportEmail || response.data?.info?.email || ''
      });
    } catch (_error) {
      setAccountDeletionContact({
        phone: '',
        supportEmail: ''
      });
    }
  };

  const submitAccountDeletionRequest = async () => {
    setAccountDeleteLoading(true);
    setAccountDeleteError('');
    setAccountDeleteMessage('');

    try {
      const response = await api.post('/auth/account-deletion-request', {
        reason: accountDeleteReason
      });

      if (response.data?.contact) {
        setAccountDeletionContact({
          phone: response.data.contact.phone || accountDeletionContact.phone,
          supportEmail: response.data.contact.supportEmail || accountDeletionContact.supportEmail
        });
      }

      setAccountDeleteMessage(response.data?.message || 'Хүсэлт амжилттай илгээгдлээ');
      setAccountDeleteReason('');
    } catch (requestError) {
      const responseData = requestError.response?.data;
      setAccountDeleteError(responseData?.message || 'Хүсэлт илгээх үед алдаа гарлаа');
      if (responseData?.contact) {
        setAccountDeletionContact({
          phone: responseData.contact.phone || accountDeletionContact.phone,
          supportEmail: responseData.contact.supportEmail || accountDeletionContact.supportEmail
        });
      }
    } finally {
      setAccountDeleteLoading(false);
    }
  };

  const syncWalletBalance = (balance) => {
    const numericBalance = Number(balance || 0);
    setWalletSummary((prev) => ({ ...prev, balance: numericBalance }));
    updateUser((prev) => (prev ? { ...prev, walletBalance: numericBalance } : prev));
  };

  const maskedWalletBalance = '••••••';
  const visibleWalletBalance = `${Number(user?.walletBalance || 0).toLocaleString()} ₮`;
  const visibleWalletSummaryBalance = `${Number(walletSummary.balance || 0).toLocaleString()} ₮`;

  const handleWalletWithdraw = async (event) => {
    event.preventDefault();
    resetWalletFeedback();
    setWalletLoading(true);

    try {
      const response = await api.post('/wallet/withdraw', {
        amount: Number(walletWithdrawForm.amount),
        bankName: walletWithdrawForm.bankName,
        iban: walletWithdrawForm.iban,
        bankAccountNumber: walletWithdrawForm.bankAccountNumber,
        walletPin: walletWithdrawForm.walletPin
      });
      setWalletMessage(response.data?.message || 'Таталтын хүсэлт илгээгдлээ');
      setWalletWithdrawForm({ bankName: '', iban: '', bankAccountNumber: '', amount: '', walletPin: '' });
    } catch (requestError) {
      setWalletError(requestError.response?.data?.message || 'Таталтын хүсэлт илгээх үед алдаа гарлаа');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleWalletTransfer = async (event) => {
    event.preventDefault();
    resetWalletFeedback();
    setWalletLoading(true);

    try {
      const response = await api.post('/wallet/transfer', {
        targetUserId: walletTransferForm.targetUserId,
        amount: Number(walletTransferForm.amount),
        walletPin: walletTransferForm.walletPin
      });
      syncWalletBalance(response.data?.balance);
      setWalletMessage(response.data?.message || 'Шилжүүлэг амжилттай');
      setWalletTransferForm({ targetUserId: '', amount: '', walletPin: '' });
    } catch (requestError) {
      setWalletError(requestError.response?.data?.message || 'Шилжүүлэг хийх үед алдаа гарлаа');
    } finally {
      setWalletLoading(false);
    }
  };


  return (
    <header className={`header header-default${variant === 'landing' ? ' header-landing' : ''}${user ? ' header-authenticated' : ''}`}
      style={{paddingBottom:0}}>
      {/* Top row: Khuleg left, search center, cart & user right */}
      <div className="header-top" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:0,padding:'0 0 12px 0'}}>
        {/* Left: Khuleg Logo + Brand + Service Booking */}
        <div style={{display:'flex',alignItems:'center',minWidth:220,gap:56}}>
          <KhulegLogo style={{width:130,height:130,marginRight:36}} />
          <button
            style={{
              fontSize:22,
              padding:'16px 44px',
              borderRadius:32,
              border:'1.5px solid #1e293b',
              background:'#fff',
              fontWeight:700,
              marginLeft:44,
              cursor:'pointer',
              boxShadow:'0 4px 16px rgba(0,0,0,0.08)'
            }}
            onClick={() => setServiceBookingModalOpen(true)}
          >
            Засварын цаг авах
          </button>
        </div>
      {serviceBookingModalOpen && (
        <ServiceBookingModal onClose={() => setServiceBookingModalOpen(false)} />
      )}
        {/* Center: Search bar */}
        <div style={{flex:1,display:'flex',justifyContent:'center',marginLeft:10,marginRight:10}}>
          <input className="header-search-wide" type="text" placeholder="Хайлт..." style={{width:'100%',maxWidth:700,padding:'14px 38px',borderRadius:32,border:'1.5px solid #e5e7eb',fontSize:22,background:'#f8fafc',boxShadow:'0 2px 8px rgba(0,0,0,0.03)',height:54}} />
        </div>
        {/* Right: Cart, then user menu */}
        <div style={{display:'flex',alignItems:'center',gap:16,minWidth:220,justifyContent:'flex-end'}}>
          <Link to="/cart" className="header-cart-link" aria-label="Сагс">
            <HiOutlineShoppingCart style={{width:28, height:28, color:'#222'}} />
          </Link>
          {!user ? (
            <div className="auth-actions auth-actions-guest">
              <div className="auth-primary-row">
                <button onClick={openLogin}>Нэвтрэх</button>
                <button onClick={openRegister}>Бүртгүүлэх</button>
              </div>
              <button className="forgot-link" onClick={openForgot} style={{background:'none',border:'none',color:'#222',textDecoration:'underline',fontSize:15,cursor:'pointer',padding:0,marginTop:6}}>
                нууц үг мартсан
              </button>
            </div>
          ) : (
            <div className="profile-menu-wrap">
              <button
                className="profile-menu-trigger"
                type="button"
                onClick={() => setProfilePanelOpen(true)}
                style={{padding:'0 14px',height:38,borderRadius:20,border:'1px solid #e5e7eb',background:'#fff',fontWeight:500,fontSize:16,marginLeft:8}}
              >
                <span style={{marginRight:8}}>{user.username}</span>
                <HiOutlineUser style={{width:18, height:18, color:'#222'}} />
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Зар нэмэх цэс зөвхөн /admin-dashboard дээр */}
      {location.pathname === '/admin-dashboard' && (
        <div className="quick-create-row">
          <Link
            to="/ads-upload"
            className="quick-create-btn"
          >
            Зар Нэмэх
          </Link>
        </div>
      )}

      {profilePanelOpen && (
        <div className="profile-panel-overlay" onClick={() => setProfilePanelOpen(false)}>
          <aside className="profile-panel" onClick={(event) => event.stopPropagation()}>
            <div className="profile-panel-header">
              <h3>Хэрэглэгчийн цэс</h3>
              <button
                type="button"
                className="profile-panel-close"
                onClick={() => {
                  setProfilePanelOpen(false);
                  setShowProfileInfo(false);
                  setWalletMenuOpen(false);
                }}
              >
                x
              </button>
            </div>

            <div className="profile-avatar-wrap">
              {profilePreviewUrl ? (
                <img src={profilePreviewUrl} alt="Profile" className="profile-avatar" />
              ) : null}
              {/* /admin-dashboard дээр нэвтрэх, бүртгүүлэх, профайл цэсийг нуух */}
              {location.pathname !== '/admin-dashboard' && (
                !user ? (
                  <div className="auth-actions auth-actions-guest">
                    <div className="auth-primary-row">
                      <button onClick={openLogin}>Нэвтрэх</button>
                      <button onClick={openRegister}>Бүртгүүлэх</button>
                    </div>
                    <button className="forgot-link" onClick={openForgot} style={{background:'none',border:'none',color:'#222',textDecoration:'underline',fontSize:15,cursor:'pointer',padding:0,marginTop:6}}>
                      нууц үг мартсан
                    </button>
                  </div>
                ) : (
                  <div className="profile-menu-wrap">
                    <button
                      className="profile-menu-trigger"
                      type="button"
                      onClick={() => setProfilePanelOpen(true)}
                      style={{padding:'0 14px',height:38,borderRadius:20,border:'1px solid #e5e7eb',background:'#fff',fontWeight:500,fontSize:16,marginLeft:8}}
                    >
                      <span style={{marginRight:8}}>{user.username}</span>
                      <HiOutlineUser style={{width:18, height:18, color:'#222'}} />
                    </button>
                  </div>
                )
              )}
            </div>
            <ul>
              <li>
                <button type="button">
                  <HiOutlineDocumentText style={{width:18, height:18, marginRight:4, color:'#ef4444'}} />
                  НӨАТ Баримт
                </button>
              </li>
              <li>
                <button type="button" onClick={openDevicesModal}>
                  <HiOutlineDevicePhoneMobile style={{width:18, height:18, marginRight:4, color:'#8b5cf6'}} />
                  Бүртгэлтэй төхөөрөмжүүд
                </button>
              </li>
              <li>
                <button type="button" onClick={openHistoryModal}>
                  <HiOutlineClock style={{width:18, height:18, marginRight:4, color:'#f59e0b'}} />
                  Үйлчилгээ авсан түүх
                </button>
              </li>
              <li><Link to="/market" style={{display:'flex', alignItems:'center', gap:4}}><HiOutlineShoppingBag style={{width:18, height:18, marginRight:4, color:'#10b981'}} />Маркет</Link></li>
              <li><Link to="/cart" style={{display:'flex', alignItems:'center', gap:4}}><HiOutlineShoppingCart style={{width:18, height:18, marginRight:4, color:'#06b6d4'}} />Сагс</Link></li>
              <li><Link to="/orders" style={{display:'flex', alignItems:'center', gap:4}}><HiOutlineDocumentText style={{width:18, height:18, marginRight:4, color:'#6366f1'}} />Захиалгын түүх</Link></li>
              <li style={{position:'relative'}}>
                <button type="button" onClick={() => setCarMenuOpen(v => !v)} style={{display:'inline-flex',alignItems:'center',gap:6}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle'}} xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="10" width="18" height="7" rx="2" stroke="#222" strokeWidth="1.5"/>
                    <circle cx="7" cy="17" r="2" stroke="#222" strokeWidth="1.5"/>
                    <circle cx="17" cy="17" r="2" stroke="#222" strokeWidth="1.5"/>
                    <rect x="7" y="7" width="10" height="3" rx="1.5" stroke="#222" strokeWidth="1.2"/>
                  </svg>
                  Миний автомашинууд
                </button>
                {carMenuOpen && (
                  <ul style={{position:'absolute',left:0,top:'100%',background:'#fff',boxShadow:'0 2px 12px rgba(0,0,0,0.08)',borderRadius:8,padding:'10px 0',margin:0,zIndex:1000,minWidth:220}}>
                    <li><button type="button" onClick={() => {setAddCarModalOpen(true); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M10 4v12M4 10h12" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/></svg>Машин нэмэх</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M4 10h12M10 4v12" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/></svg>Машины жагсаалт</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/service-history'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M6 14l4-4 4 4" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/></svg>Үйлчилгээний түүх</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/parts'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><rect x="4" y="7" width="12" height="6" rx="2" stroke="#222" strokeWidth="1.5"/><circle cx="10" cy="10" r="2" stroke="#222" strokeWidth="1.5"/></svg>Сэлбэг, дагалдах</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/loan'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><rect x="4" y="7" width="12" height="6" rx="2" stroke="#222" strokeWidth="1.5"/><path d="M10 7v6" stroke="#222" strokeWidth="1.5"/></svg>Зээлийн үйлчилгээ</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/diagnostics'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><rect x="4" y="7" width="12" height="6" rx="2" stroke="#222" strokeWidth="1.5"/><path d="M7 10h6" stroke="#222" strokeWidth="1.5"/></svg>Оношилгоо</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/documents'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><rect x="4" y="7" width="12" height="6" rx="2" stroke="#222" strokeWidth="1.5"/><circle cx="10" cy="10" r="2" stroke="#222" strokeWidth="1.5"/></svg>Зураг, баримт бичиг</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/reminders'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M10 4v12" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/><circle cx="10" cy="16" r="1.5" stroke="#222" strokeWidth="1.5"/></svg>Сануулга</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/market'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><rect x="4" y="7" width="12" height="6" rx="2" stroke="#222" strokeWidth="1.5"/><path d="M7 10h6" stroke="#222" strokeWidth="1.5"/></svg>Зарын зах зээл</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/owner'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7" stroke="#222" strokeWidth="1.5"/><path d="M10 7v6" stroke="#222" strokeWidth="1.5"/></svg>Эзэмшигч</button></li>
                    <li><button type="button" onClick={() => {navigate('/cars/feedback'); setCarMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:6,width:'100%',background:'none',border:'none',padding:'8px 18px',textAlign:'left',cursor:'pointer'}}><svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M10 15l4-4-4-4" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/></svg>Үнэлгээ, санал хүсэлт</button></li>
                  </ul>
                )}
              </li>
              <li>
                <button type="button" onClick={openContact}>
                  <HiOutlineEnvelope style={{width:18, height:18, marginRight:4, color:'#ec4899'}} />
                  Холбоо барих
                </button>
              </li>
            </ul>
            {addCarModalOpen && (
              <AddCarModal onClose={() => setAddCarModalOpen(false)} />
            )}
            <div className="profile-panel-logout">
              <button
                type="button"
                className="profile-panel-logout-btn"
                onClick={() => { setProfilePanelOpen(false); setShowProfileInfo(false); logout(); }}
              >
                Гарах
              </button>
            </div>
          </aside>
        </div>
      )}

      {profileModalOpen && (
        <div
          className="modal-backdrop"
          onClick={() => { setProfileModalOpen(false); setActiveProfileSection(null); setWalletMenuOpen(false); }}
        >
          <div
            className="modal profile-info-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-info-modal-header">
              <div className="profile-info-modal-title-row">
                <h3>Миний профайл</h3>
                <div className="profile-status-wrap">
                  <span className="profile-status-text">Статус:</span>
                  {isAdmin ? (
                    isExternalAdminPortal ? (
                      <a
                        href={adminPortalUrl}
                        className="profile-status-inline profile-status-link"
                        onClick={() => {
                          setProfileModalOpen(false);
                          setProfilePanelOpen(false);
                          setActiveProfileSection(null);
                        }}
                      >
                        <span className="profile-status-icon" aria-hidden="true">{profileRoleIcon}</span>
                        <span className="profile-status-role">{profileRoleLabel}</span>
                      </a>
                    ) : (
                      <Link
                        to={adminPortalUrl}
                        className="profile-status-inline profile-status-link"
                        onClick={() => {
                          setProfileModalOpen(false);
                          setProfilePanelOpen(false);
                          setActiveProfileSection(null);
                        }}
                      >
                        <span className="profile-status-icon" aria-hidden="true">{profileRoleIcon}</span>
                        <span className="profile-status-role">{profileRoleLabel}</span>
                      </Link>
                    )
                  ) : (
                    <span className="profile-status-inline">
                      <span className="profile-status-icon" aria-hidden="true">{profileRoleIcon}</span>
                      <span className="profile-status-role">{profileRoleLabel}</span>
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="profile-panel-close"
                onClick={() => { setProfileModalOpen(false); setActiveProfileSection(null); setWalletMenuOpen(false); }}
              >
                ✕
              </button>
            </div>

            <div className="profile-info-sections">
              {profileSections.map((section) => (
                <div key={section.key} className="profile-info-row">
                  {section.key === 'wallet' ? (
                    <button
                      type="button"
                      className="profile-info-row-label profile-info-row-label-btn"
                      onClick={() => setWalletMenuOpen(true)}
                    >
                      {section.label}
                    </button>
                  ) : section.key === 'devices' ? (
                    <button
                      type="button"
                      className="profile-info-row-label profile-info-row-label-btn"
                      onClick={openDevicesModal}
                    >
                      {section.label}
                    </button>
                  ) : (
                    <div className="profile-info-row-label">{section.label}</div>
                  )}
                  <div className="profile-info-row-value">
                    {section.key === 'username' && <span>{user?.username}</span>}
                    {section.key === 'email' && <span>{user?.email}</span>}
                    {section.key === 'phone' && <span>{user?.phone || '—'}</span>}
                    {section.key === 'password' && (
                      <span>{passwordVisible ? (user?.password || 'mySecretPassword123') : '••••••••'}</span>
                    )}
                    {section.key === 'devices' && <span>{registeredDevices.length || 0} төхөөрөмж</span>}
                    {section.key === 'wallet' && (
                      <div className="profile-secret-wrap">
                        <button
                          type="button"
                          className="wallet-open-trigger"
                          onClick={() => {
                            setWalletMenuOpen(true);
                            setWalletAction('');
                            resetWalletFeedback();
                          }}
                        >
                          {walletBalanceVisible ? visibleWalletBalance : maskedWalletBalance}
                        </button>
                        <button
                          type="button"
                          className="secret-toggle-btn"
                          onClick={() => setWalletBalanceVisible((prev) => !prev)}
                          aria-label={walletBalanceVisible ? 'Хэтэвчийн дүн нуух' : 'Хэтэвчийн дүн харуулах'}
                        >
                          <EyeIcon open={walletBalanceVisible} />
                        </button>
                      </div>
                    )}
                  </div>
                  {section.key !== 'wallet' && section.key !== 'devices' && section.key !== 'publicUserId' && (
                    <div className="profile-info-row-actions">
                      {section.key === 'password' ? (
                        <button
                          type="button"
                          className="secret-toggle-btn"
                          onClick={() => setPasswordVisible((prev) => !prev)}
                          aria-label={passwordVisible ? 'Нууц үгийн тайлбар нуух' : 'Нууц үгийн тайлбар харуулах'}
                        >
                          <EyeIcon open={passwordVisible} />
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="profile-section-menu-btn"
                            onClick={(e) => toggleProfileSection(section.key, e)}
                          >
                            ···
                          </button>
                          {activeProfileSection === section.key && (
                            <div className="profile-section-menu-dropdown">
                              <button type="button" onClick={() => setActiveProfileSection(null)}>Сэргээх</button>
                              <button type="button" onClick={() => setActiveProfileSection(null)}>Солих</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div className="profile-danger-box">
                <div className="profile-danger-copy">
                  <strong>Аккаунт устгах</strong>
                  <span>Өөрөө шууд устгах боломжгүй. Админд хүсэлт гаргана.</span>
                </div>
                <button
                  type="button"
                  className="profile-danger-btn"
                  onClick={openAccountDeletionModal}
                >
                  Аккаунт устгах хүсэлт
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {devicesModalOpen && (
        <div className="modal-backdrop" onClick={() => setDevicesModalOpen(false)}>
          <div className="modal device-sessions-modal" onClick={(event) => event.stopPropagation()}>
            <div className="wallet-menu-header">
              <h3>Бүртгэлтэй төхөөрөмжүүд</h3>
              <button type="button" className="profile-panel-close" onClick={() => setDevicesModalOpen(false)}>✕</button>
            </div>

            {devicesLoading ? (
              <p>Уншиж байна...</p>
            ) : (
              <div className="device-sessions-list">
                {registeredDevices.length === 0 ? (
                  <p className="wallet-topup-note">Бүртгэлтэй төхөөрөмж олдсонгүй.</p>
                ) : (
                  registeredDevices.map((device) => (
                    <div key={device.sessionId} className="device-session-item">
                      <div className="device-session-main">
                        <strong>{device.label || 'Unknown device'}</strong>
                        <span>IP: {device.ip || '-'}</span>
                        <span>Сүүлд идэвхтэй: {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString('mn-MN') : '-'}</span>
                      </div>
                      <div className="device-session-actions">
                        {device.isCurrent ? (
                          <span className="device-session-current">Одоогийн төхөөрөмж</span>
                        ) : device.revokedAt ? (
                          <span className="device-session-revoked">Салгасан</span>
                        ) : (
                          <button
                            type="button"
                            className="wallet-pin-secondary-btn"
                            onClick={() => disconnectDeviceSession(device.sessionId)}
                          >
                            Холболт салгах
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {devicesError && <p className="error">{devicesError}</p>}
            {devicesMessage && <p className="success">{devicesMessage}</p>}
          </div>
        </div>
      )}

      {historyModalOpen && (
        <div className="modal-backdrop" onClick={() => setHistoryModalOpen(false)}>
          <div className="modal history-modal" onClick={(event) => event.stopPropagation()}>
            <div className="wallet-menu-header">
              <h3>Үзсэн түүх</h3>
              <button type="button" className="profile-panel-close" onClick={() => setHistoryModalOpen(false)}>✕</button>
            </div>

            <div className="history-toolbar">
              <span>{viewHistoryItems.length} бүртгэл</span>
              <button type="button" className="wallet-pin-secondary-btn" onClick={clearHistory} disabled={historyLoading || viewHistoryItems.length === 0}>
                Бүгдийг цэвэрлэх
              </button>
            </div>

            {historyLoading ? (
              <p>Уншиж байна...</p>
            ) : viewHistoryItems.length === 0 ? (
              <p className="wallet-topup-note">Одоогоор үзсэн түүх алга.</p>
            ) : (
              <div className="history-list">
                {viewHistoryItems.map((item) => (
                  <div key={item._id} className="history-item">
                    <div className="history-item-preview-wrap">
                      {item.previewImageUrl && looksLikeImageAsset(item.previewImageUrl) ? (
                        <img
                          className="history-item-preview"
                          src={resolveMediaUrl(item.previewImageUrl)}
                          alt={item.title}
                        />
                      ) : (
                        <div className="history-item-preview history-item-preview-fallback">
                          {item.itemType === 'movie' ? 'VIDEO' : 'CONTENT'}
                        </div>
                      )}
                    </div>
                    <div className="history-item-main">
                      <strong>{item.title}</strong>
                      <span>{item.itemType === 'movie' ? 'Кино / Медиа' : item.section || 'Контент'}</span>
                      <span>{item.category || 'Ангилалгүй'}</span>
                      <span>Сүүлд үзсэн: {formatHistoryTime(item.lastViewedAt)}</span>
                      <span>Нийт үзсэн: {Number(item.viewCount || 0)}</span>
                    </div>
                    <div className="history-item-actions">
                      <button type="button" className="wallet-pin-primary-btn" onClick={() => openHistoryItem(item)}>
                        Нээх
                      </button>
                      <button type="button" className="wallet-pin-secondary-btn" onClick={() => removeHistoryItem(item._id)}>
                        Устгах
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {historyError && <p className="error">{historyError}</p>}
            {historyMessage && <p className="success">{historyMessage}</p>}
          </div>
        </div>
      )}

      {likedModalOpen && (
        <MyPartOrdersModal onClose={() => setLikedModalOpen(false)} isAdmin={role === 'admin'} />
      )}

      {accountDeleteModalOpen && (
        <div
          className="modal-backdrop"
          onClick={() => {
            setAccountDeleteModalOpen(false);
            setAccountDeleteError('');
            setAccountDeleteMessage('');
          }}
        >
          <div className="modal account-delete-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Аккаунт устгах хүсэлт</h3>
            <p className="account-delete-policy">
              Хэрэглэгч өөрөө бүртгэлээ шууд устгах боломжгүй.
              Устгуулах бол админд утсаар эсвэл support мэйлээр хүсэлт гаргана.
            </p>
            <div className="account-delete-contact">
              <span>Утас: {accountDeletionContact.phone || '-'}</span>
              <span>Мэйл: {accountDeletionContact.supportEmail || '-'}</span>
            </div>
            <div className="form">
              <textarea
                placeholder="Хүсэлтийн тайлбар (заавал биш)"
                value={accountDeleteReason}
                onChange={(event) => setAccountDeleteReason(event.target.value)}
                rows={4}
              />
              <div className="wallet-form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setAccountDeleteModalOpen(false);
                    setAccountDeleteError('');
                    setAccountDeleteMessage('');
                  }}
                >
                  Хаах
                </button>
                <button
                  type="button"
                  disabled={accountDeleteLoading}
                  onClick={submitAccountDeletionRequest}
                >
                  {accountDeleteLoading ? 'Илгээж байна...' : 'Хүсэлт илгээх'}
                </button>
              </div>
              {accountDeleteError && <p className="error">{accountDeleteError}</p>}
              {accountDeleteMessage && <p className="success">{accountDeleteMessage}</p>}
            </div>
          </div>
        </div>
      )}

      {walletMenuOpen && (
        <div className="modal-backdrop" onClick={() => setWalletMenuOpen(false)}>
          <div className="modal wallet-menu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-menu-header">
              <h3>Хэтэвч</h3>
              <button type="button" className="profile-panel-close" onClick={() => setWalletMenuOpen(false)}>✕</button>
            </div>
            <div className="wallet-balance-chip-wrap">
              <div className="wallet-balance-chip">Үлдэгдэл: {walletBalanceVisible ? visibleWalletSummaryBalance : maskedWalletBalance}</div>
              <button
                type="button"
                className="secret-toggle-btn"
                onClick={() => setWalletBalanceVisible((prev) => !prev)}
                aria-label={walletBalanceVisible ? 'Үлдэгдэл нуух' : 'Үлдэгдэл харуулах'}
              >
                <EyeIcon open={walletBalanceVisible} />
              </button>
            </div>

            {!walletAction && (
              <div className="wallet-menu-grid">
                <button type="button" onClick={() => { setWalletAction('topup'); resetWalletFeedback(); }}>Цэнэглэх</button>
                <button type="button" onClick={() => { setWalletAction('withdraw'); resetWalletFeedback(); }}>Татах</button>
                <button type="button" onClick={() => { setWalletAction('transfer'); resetWalletFeedback(); }}>Шилжүүлэг</button>
              </div>
            )}

            {walletAction === 'topup' && (
              <div className="wallet-form">
                <div className="wallet-admin-bank-box">
                  <strong>Админ данс</strong>
                  <span>{walletSummary.adminBankName || 'Банк тохируулаагүй'}</span>
                  <span>{walletSummary.adminIban || 'IBAN тохируулаагүй'}</span>
                  <span>{walletSummary.adminBankAccountNumber || 'Данс тохируулаагүй'}</span>
                </div>
                <p className="wallet-topup-note">
                  Банк апп-аасаа дээрх админ данс руу шилжүүлэг хийнэ үү.
                </p>
                <div className="wallet-form-actions">
                  <button type="button" onClick={() => setWalletAction('')}>Буцах</button>
                  <button type="button" onClick={() => setWalletMenuOpen(false)}>Хаах</button>
                </div>
              </div>
            )}

            {walletAction === 'withdraw' && (
              <form className="wallet-form" onSubmit={handleWalletWithdraw}>
                <input
                  value={walletWithdrawForm.bankName}
                  onChange={(event) => setWalletWithdrawForm((prev) => ({ ...prev, bankName: event.target.value }))}
                  placeholder="Банкны нэр"
                  required
                />
                <input
                  value={walletWithdrawForm.iban}
                  onChange={(event) => setWalletWithdrawForm((prev) => ({ ...prev, iban: event.target.value }))}
                  placeholder="IBAN"
                  inputMode="numeric"
                />
                <input
                  value={walletWithdrawForm.bankAccountNumber}
                  onChange={(event) => setWalletWithdrawForm((prev) => ({ ...prev, bankAccountNumber: event.target.value }))}
                  placeholder="Таны дансны дугаар"
                  required
                />
                <input
                  type="number"
                  min="1"
                  value={walletWithdrawForm.amount}
                  onChange={(event) => setWalletWithdrawForm((prev) => ({ ...prev, amount: event.target.value }))}
                  placeholder="Татах дүн"
                  required
                />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={walletWithdrawForm.walletPin}
                  onChange={(event) => setWalletWithdrawForm((prev) => ({ ...prev, walletPin: event.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  placeholder="PIN код (4 оронтой)"
                  autoComplete="current-password"
                  required
                />
                <div className="wallet-form-actions">
                  <button type="button" onClick={() => setWalletAction('')}>Буцах</button>
                  <button type="submit" disabled={walletLoading}>Таталтын хүсэлт</button>
                </div>
              </form>
            )}

            {walletAction === 'transfer' && (
              <form className="wallet-form" onSubmit={handleWalletTransfer}>
                <input
                  value={walletTransferForm.targetUserId}
                  onChange={(event) => setWalletTransferForm((prev) => ({ ...prev, targetUserId: event.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="Хүлээн авагчийн 6 оронтой ID"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                />
                <input
                  type="number"
                  min="1"
                  value={walletTransferForm.amount}
                  onChange={(event) => setWalletTransferForm((prev) => ({ ...prev, amount: event.target.value }))}
                  placeholder="Шилжүүлэх дүн"
                  required
                />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={walletTransferForm.walletPin}
                  onChange={(event) => setWalletTransferForm((prev) => ({ ...prev, walletPin: event.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  placeholder="PIN код (4 оронтой)"
                  autoComplete="current-password"
                  required
                />
                <div className="wallet-form-actions">
                  <button type="button" onClick={() => setWalletAction('')}>Буцах</button>
                  <button type="submit" disabled={walletLoading}>Илгээх</button>
                </div>
              </form>
            )}

            {walletMessage && <p className="success">{walletMessage}</p>}
            {walletError && <p className="error">{walletError}</p>}
          </div>
        </div>
      )}

      {localModal === 'login' && (
        <LoginModal
          onClose={() => setLocalModal('')}
          onOpenForgot={openForgot}
          onOpenForgotPassword={() => setLocalModal('forgot-password')}
          onOpenForgotUsername={() => setLocalModal('forgot-username')}
          onOpenRegister={openRegister}
        />
      )}
      {localModal === 'register' && <RegisterModal onClose={() => setLocalModal('')} />}
      {localModal === 'forgot-password' && <ForgotPasswordModal onClose={() => setLocalModal('')} />}
      {localModal === 'forgot-username' && <ForgotUsernameModal onClose={() => setLocalModal('')} />}
      {localModal === 'contact' && <Contact onClose={() => setLocalModal('')} />}
      {localModal === 'contact-phone' && <Contact mode="phone" onClose={() => setLocalModal('')} />}
      {localModal === 'contact-email' && <Contact mode="email" onClose={() => setLocalModal('')} />}
      {localModal === 'chat' && <ChatModal onClose={() => setLocalModal('')} />}

      {localModal === 'forgot-choice' && (
        <div className="modal-backdrop" onClick={() => setLocalModal('')}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3>Сэргээх сонголт</h3>
            <div className="form">
              <button onClick={() => setLocalModal('forgot-username')}>Нэвтрэх нэр мартсан</button>
              <button onClick={() => setLocalModal('forgot-password')}>Нууц үг сэргээх</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
